import React, {
  useContext,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {
  GLOBAL_TRANSLATION_NAMESPACE,
  SECONDARY,
} from 'consts';
import { Context } from 'context/Context';
import {
  EnelXGuiAtoms, EnelXGuiComponents, EnelXGuiPropTypes, EnelXGuiUtils,
} from 'gui';
import useFormatter from 'hook/useFormatter';
import PropTypes from 'prop-types';

import AllFilter from './AllFilter';
import SearchbarWrapper from './style';
import {
  getDefaultActiveFilter, typeMapper,
} from './typeMapper';

const { SearchbarValueShape, SearchbarOutputShape } = EnelXGuiPropTypes;
const { arrUpsert } = EnelXGuiUtils;
const { MigrationSearchbar } = EnelXGuiComponents;
const { MigrationButton } = EnelXGuiAtoms;

const CustomSearchbar = ({
  submit,
  values,
  filters,
  settingCb,
  visibleIds,
  customButtonsBefore,
  isV2Report,
  templateCb,
}) => {
  const { epoch } = useFormatter();
  const { t } = useTranslation();
  const {
    localization: {
      countryID,
    },
  } = useContext(Context);
  const defaultFilters = values.reduce((acc, { id, defaultValue }) => {
    if (defaultValue) {
      acc.push({ id, value: defaultValue });
    }
    return acc;
  }, []);

  const [actualFilter, setActualFilter] = useState(getDefaultActiveFilter(values)); // this is searchbar (multiinput)'s component stuffvs
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const createElementsFromConf = ({ outputs }, ids) => values.reduce((acc, curr) => {
    const currentElement = typeMapper({ countryID, epoch })[curr.type];
    if (!currentElement) {
      // eslint-disable-next-line no-console
      console.error(`Type not found: ${curr.type}`);
      return acc;
    }
    const elementComponentProps = currentElement({
      outputs,
      actualFilter,
      setActualFilter,
      visibleIds: ids,
      // E IS BEING SPREAD BECAUSE IT MIGHT CONTAIN SOME PROPS PASSED TO EVERY TIPE OF RENDERED ELEMENTS
      ...curr,
    });
    return acc.concat({
      id: curr.id,
      label: curr.label,
      defaultValue: curr.defaultValue,
      disableChipClose: curr.disableChipClose,
      ...elementComponentProps,
    });
  }, []);
  const filterVisibleElements = ({ outputs }) => {
    const allElements = createElementsFromConf({ outputs }, visibleIds);
    if (!visibleIds) return allElements;
    return allElements.map((element) => {
      const toRet = { ...element, hidden: false };
      if (element.componentProps.type === 'input') {
        const arr = element.componentProps.arr.map((opt) => ({ ...opt, hidden: visibleIds.indexOf(opt.id) === -1 }));
        toRet.componentProps.arr = arr;
        if (arr.every((opt) => opt.hidden)) {
          toRet.hidden = true;
        }
      }
      if (visibleIds.indexOf(element.id) === -1) {
        toRet.hidden = true;
      }
      return toRet;
    });
  };

  const handleSubmit = (arr) => submit(arrUpsert(defaultFilters, arr));

  const getExtraElements = () => {
    const toRet = [];
    if (customButtonsBefore) {
      toRet.push(...customButtonsBefore);
    }
    if (visibleIds) {
      toRet.push(
        <MigrationButton
          btnShape="square"
          btnType={SECONDARY}
          iconName="filters"
          iconSize="1em"
          key="SEARCH_BAR_ALL_FILTERS"
          onClick={toggleSidebar}
        />,
      );
    }
    return toRet;
  };

  return (
    <SearchbarWrapper isMobile={isMobileOnly}>
      <MigrationSearchbar
        filters={arrUpsert(defaultFilters, filters)}
        onSubmit={handleSubmit}
        extraElements={getExtraElements}
        settingsCb={settingCb ? () => {
          settingCb();
        } : null}
        labels={{
          submit: t(isV2Report ? 'confirm' : `${GLOBAL_TRANSLATION_NAMESPACE}:components.search`),
          reset: t('components.reset'),
        }}
        templateCb={templateCb}
        elements={filterVisibleElements}
      />
      {
        visibleIds
          ? (
            <AllFilter
              isSidebarOpen={isSidebarOpen}
              close={toggleSidebar}
              elements={createElementsFromConf}
              filters={arrUpsert(defaultFilters, filters)}
              onSubmit={handleSubmit}
              defaultFilters={defaultFilters}
            />
          )
          : null
      }
      <MigrationButton
        btnShape="square"
        btnSize="wide"
        btnType="secondary"
        iconName="bookmark-add"
        iconSize="1.5em"
        id="templateManagementBtn"
        onClick={templateCb}
      />
    </SearchbarWrapper>
  );
};

CustomSearchbar.defaultProps = {
  customButtonsBefore: null,
  filters: [],
  isV2Report: false,
  settingCb: null,
  templateCb: null,
  values: [],
  visibleIds: null,
};

CustomSearchbar.propTypes = {
  submit: PropTypes.func.isRequired,
  visibleIds: PropTypes.arrayOf(PropTypes.string),
  settingCb: PropTypes.func,
  filters: SearchbarOutputShape,
  customButtonsBefore: PropTypes.arrayOf(PropTypes.node),
  values: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: SearchbarValueShape,
          label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      ),
      defaultValue: SearchbarValueShape,
      disableChipClose: PropTypes.bool,
    }),
  ),
  isV2Report: PropTypes.bool,
  templateCb: PropTypes.func,
};

CustomSearchbar.defaultName = 'CustomSearchbar';

export default CustomSearchbar;
